import React, { useState, useEffect, useCallback } from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  InputBase,
  TablePagination,
  Tooltip,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  SCREEN_IDS,
  typeConnect,
  backgroundColor2,
} from "../constants/Constants";

import { translateText } from "../translations/translationService";
import {
  getFullMoForStone,
  insertControlDePieza,
} from "../services/ConnectDataBase";
import { getCurrentDateTime } from "../services/Functions";
import { STONE_CONTROL_COLORS } from "../style/colors.js";

export default function StoneControl() {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const screenName = "StoneControlScreen";
  const currentScreenId = SCREEN_IDS.STONE_CONTROL;
  const lang = useSelector((state) => state.lang.value);
  const [searchedPO, setSearchedPO] = useState("");
  const usuario = useSelector((state) => state.user.name);
  const [refSearch, setRefSearch] = useState("");
  const [listData, setListData] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [searchOK, setSearchOk] = useState(false);

  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 40);


  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMoForStone(
        screenName,
        typeConnect[0],
        currentScreenId
      );
      const groupedData = groupDataByMo(fullMo.data);
      setListData(groupedData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, []);

  const groupDataByMo = (data) => {
    const groupedData = {};

    data.forEach((item) => {
      if (!groupedData[item.mo]) {
        groupedData[item.mo] = {
          po: item.po,
          mo: item.mo,
          codigo: item.codigo,
          numeropiedras: parseFloat(item.numeropiedras),
          usuario_ok_piedras: item.usuario_ok_piedras,
          usuario_ok_taller: item.usuario_ok_taller,
          existe_bagging_completo: item.existe_bagging_completo === "true",
          existe_entrada_piedras: item.existe_entrada_piedras === "true",
          existe_salida_piedras_ent: item.existe_salida_piedras_ent === "true",
        };
      } else {
        groupedData[item.mo].numeropiedras += parseFloat(item.numeropiedras);
        groupedData[item.mo].codigo += "," + item.codigo;
        groupedData[item.mo].usuario_ok_piedras =
          groupedData[item.mo].usuario_ok_piedras || item.usuario_ok_piedras;
        groupedData[item.mo].usuario_ok_taller =
          groupedData[item.mo].usuario_ok_taller || item.usuario_ok_taller;
        groupedData[item.mo].existe_bagging_completo =
          groupedData[item.mo].existe_bagging_completo ||
          item.existe_bagging_completo === "true";
        groupedData[item.mo].existe_entrada_piedras =
          groupedData[item.mo].existe_entrada_piedras ||
          item.existe_entrada_piedras === "true";
        groupedData[item.mo].existe_salida_piedras_ent =
          groupedData[item.mo].existe_salida_piedras_ent ||
          item.existe_salida_piedras_ent === "true";
      }
    });

    return Object.values(groupedData);
  };

  useEffect(() => {
    getMo();
  }, [getMo]);

  const handleSearch = () => {
    let trimmedRefSearch = refSearch.trim();
    if (trimmedRefSearch === "") {
      setSearchOk(true);
      setSearchResult(null);
      return;
    }
    setSearchedPO(trimmedRefSearch);
    let searchTerms = trimmedRefSearch.split(" ").map((term) => term.trim());
    const result = listData.filter((item) => {
      const moModified = item.mo.replace("'", "-");
      return searchTerms.includes(moModified);
    });
    setSearchOk(true);
    setSearchResult(result);
    setRefSearch("");
  };

  const handleCompleteEntrada = async () => {
    setIsProcessingAll(true);
    const items = searchResult || listData;
    for (const item of items) {
      if (!item.existe_entrada_piedras) {
        await handleAddEntrada(item, false);
      }
    }
    setIsProcessingAll(false);
    await getMo();
    setSearchResult(null);
    setSearchOk(false);
  };

  const handleCompleteSalida = async () => {
    setIsProcessingAll(true);
    const items = searchResult || listData;
    for (const item of items) {
      await handleAddSalida(item, false);
    }
    setIsProcessingAll(false);
    await getMo();
    setSearchResult(null);
    setSearchOk(false);
  };

  const handleAddEntrada = async (item, updateList = true) => {
    const status = await insertControlDePieza(
      item.po,
      item.mo,
      usuario,
      1,
      getCurrentDateTime(),
      "EntraPiedras",
      SCREEN_IDS.PARTS_MOVEMENTS
    );
    if (status && updateList) {
      setSearchResult(null);
      setRefSearch("");
      await getMo();
      console.log("Entrada en piedras");
    }
  };

  const handleAddSalida = async (item, updateList = true) => {
    const status = await insertControlDePieza(
      item.po,
      item.mo,
      usuario,
      1,
      getCurrentDateTime(),
      "SalidaPiedras",
      SCREEN_IDS.PARTS_MOVEMENTS
    );
    if (status && updateList) {
      setSearchResult(null);
      setRefSearch("");
      await getMo();
      console.log("Salida de piedras");
    }
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${STONE_CONTROL_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            value={refSearch}
            onChange={(e) => {
              let valor = e.target.value;

              valor = valor.replace(/'/g, "-");
              valor = valor.replace(/\r?\n|\r/g, "");
              let parts = valor.split(" ");
              parts = parts.map((part) => part.replace(/\s+/g, ""));
              valor = parts.join(" ");
              const lastPart = parts[parts.length - 1];

              if (
                lastPart &&
                lastPart.length === 10 &&
                !valor.endsWith(" ")
              ) {
                valor += " ";
              }

              setRefSearch(valor);
            }}
            placeholder={translateText("Código de MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${STONE_CONTROL_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: STONE_CONTROL_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: STONE_CONTROL_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: STONE_CONTROL_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: STONE_CONTROL_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={handleSearch}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: STONE_CONTROL_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Control de Piedras", lang)}
        </Typography>

        <Button
          variant='contained'
          onClick={handleCompleteEntrada}
          disabled={!searchOK}
          size="small"
          sx={{
            backgroundColor: searchOK ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_PRIMARY : STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
            color: "white",
            borderRadius: 2,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "6px 12px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: searchOK ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_SECONDARY : STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
              transform: searchOK ? "translateY(-1px)" : "none",
              boxShadow: searchOK ? "0 2px 4px rgba(70, 93, 100, 0.2)" : "none",
            },
            "&:disabled": {
              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
              color: "#9CA3AF",
            },
          }}>
          {isProcessingAll ? (
            <CircularProgress size={18} color='inherit' />
          ) : (
            translateText("Entrada/todos", lang)
          )}
        </Button>

        <Button
          variant='contained'
          onClick={handleCompleteSalida}
          disabled={!searchOK}
          size="small"
          sx={{
            backgroundColor: searchOK ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_PRIMARY : STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
            color: "white",
            borderRadius: 2,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "6px 12px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: searchOK ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_SECONDARY : STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
              transform: searchOK ? "translateY(-1px)" : "none",
              boxShadow: searchOK ? "0 2px 4px rgba(70, 93, 100, 0.2)" : "none",
            },
            "&:disabled": {
              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
              color: "#9CA3AF",
            },
          }}>
          {isProcessingAll ? (
            <CircularProgress size={18} color='inherit' />
          ) : (
            translateText("Salida/todos", lang)
          )}
        </Button>

        <IconButton
          onClick={getMo}
          sx={{
            padding: 1,
            color: STONE_CONTROL_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>

      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${STONE_CONTROL_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Código", lang),
                      translateText("Bagging", lang),
                      translateText("Acciones", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(6)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : (searchResult || listData).length === 0 ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${STONE_CONTROL_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: STONE_CONTROL_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("No hay MOs pendientes...", lang)}
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${STONE_CONTROL_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    #
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("PO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Código", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Bagging", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Acciones", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(searchResult || listData)
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        },
                        borderBottom: `1px solid ${STONE_CONTROL_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                      }}>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {index + 1 + page * rowsPerPage}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.po}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.mo}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        <Tooltip title={item.codigo} arrow>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "help",
                              maxWidth: "200px",
                            }}>
                            {item.codigo.length > 25
                              ? item.codigo.slice(0, 25) + "..."
                              : item.codigo}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: STONE_CONTROL_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.existe_bagging_completo === false
                          ? translateText("Bagging a la espera", lang)
                          : translateText("Bagging Completo", lang)}
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <Button
                          variant='contained'
                          onClick={() => handleAddEntrada(item)}
                          disabled={item.existe_entrada_piedras}
                          size="small"
                          sx={{
                            backgroundColor: item.existe_entrada_piedras
                              ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED
                              : backgroundColor2,
                            color: "white",
                            fontSize: "0.75rem",
                            padding: "4px 12px",
                            height: "28px",
                            textTransform: "none",
                            borderRadius: 1,
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: item.existe_entrada_piedras
                                ? STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED
                                : STONE_CONTROL_COLORS.BACKGROUND_BUTTON_PRIMARY,
                              transform: item.existe_entrada_piedras ? "none" : "translateY(-1px)",
                              boxShadow: item.existe_entrada_piedras ? "none" : "0 2px 4px rgba(70, 93, 100, 0.2)",
                            },
                            "&:disabled": {
                              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_DISABLED,
                              color: "#9CA3AF",
                            },
                          }}>
                          {translateText("Entrada", lang)}
                        </Button>
                        <Button
                          variant='contained'
                          onClick={() => handleAddSalida(item)}
                          size="small"
                          sx={{
                            backgroundColor: backgroundColor2,
                            color: "white",
                            fontSize: "0.75rem",
                            padding: "4px 12px",
                            height: "28px",
                            textTransform: "none",
                            borderRadius: 1,
                            boxShadow: "none",
                            ml: 1,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_BUTTON_PRIMARY,
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(70, 93, 100, 0.2)",
                            },
                          }}>
                          {translateText("Salida", lang)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[10, 40, 100]}
            component='div'
            count={(searchResult || listData).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: STONE_CONTROL_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
              "& .MuiTablePagination-toolbar": {
                fontSize: "0.75rem",
                color: STONE_CONTROL_COLORS.TEXT_SECONDARY || "#6B7280",
                minHeight: "52px",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
